import {
  BarChart3,
  Building2,
  AlertTriangle,
  ClipboardList,
  BadgeCheck,
  FileText,
  Hotel,
  LayoutDashboard,
  LogOut,
  MapPin,
  Megaphone,
  Bell,
  Gauge,
  Gift,
  Library,
  Settings,
  ShieldAlert,
  Tag,
  User,
  UsersRound,
  Users,
  Wallet,
  Wrench,
} from 'lucide-react'
import type { ComponentType } from 'react'
import { useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import { useAuthStore } from '../../store/authStore'
import { useColors } from '../../hooks/useColors'
import { adminApi } from '../../services/api'
import { userHasPermission, type Permission } from '../../lib/permissions'
import { useFeatureFlags, type FeatureFlags } from '../../hooks/useFeatureFlags'

interface NavItem {
  label: string
  to: string
  icon: ComponentType<{ size?: number; color?: string }>
  // Permission required to see this item. Omit for items every authenticated
  // admin may see (e.g. the dashboard landing page).
  perm?: Permission
  // Runtime feature flag required to see this item, on top of any permission.
  // Hidden when the flag is off (controlled from Railway via the backend).
  flag?: keyof FeatureFlags
}

interface NavSection {
  heading: string
  items: NavItem[]
}

const navSections: NavSection[] = [
  {
    heading: 'MAIN',
    items: [
      { label: 'Dashboard', to: '/', icon: LayoutDashboard },
      { label: 'Orders', to: '/orders', icon: ClipboardList, perm: 'orders.view', flag: 'adminOrders' },
      { label: 'Express', to: '/express-orders', icon: Gauge, perm: 'orders.view' },
      { label: 'Live Map', to: '/live-map', icon: MapPin, perm: 'orders.view' },
      { label: 'Agents', to: '/management?tab=agents', icon: Users, perm: 'agents.view' },
      { label: 'Partners', to: '/management?tab=partners', icon: Building2, perm: 'partners.view' },
      { label: 'KYC Approval', to: '/kyc-approval', icon: BadgeCheck, perm: 'agents.view' },
    ],
  },
  {
    heading: 'B2B BILLING',
    items: [
      { label: 'B2B Clients', to: '/b2b/clients', icon: Hotel, perm: 'b2b.view' },
      { label: 'Billing Console', to: '/b2b/billing', icon: ClipboardList, perm: 'b2b.bill' },
      { label: 'B2B Invoices', to: '/b2b/invoices', icon: FileText, perm: 'b2b.view' },
    ],
  },
  {
    heading: 'ENGAGEMENT',
    items: [
      { label: 'Notification Center', to: '/notifications', icon: Bell, perm: 'notifications.view' },
      { label: 'Content Library', to: '/notifications/templates', icon: Library, perm: 'notifications.view' },
    ],
  },
  {
    heading: 'MANAGE',
    items: [
      { label: 'Customers', to: '/customers', icon: UsersRound, perm: 'customers.view' },
      { label: 'Service Pricing', to: '/service-pricing', icon: Tag, perm: 'services.update' },
      { label: 'Service Centers', to: '/service-centers', icon: Building2, perm: 'settings.update' },
      { label: 'Delivery Zones', to: '/delivery-zones', icon: MapPin, perm: 'settings.update' },
      { label: 'Analytics', to: '/analytics', icon: BarChart3, perm: 'analytics.view' },
      { label: 'Cash', to: '/cash', icon: Wallet, perm: 'cash.view' },
      // Customer wallets, top-ups and referrals. Distinct from 'Cash', which is
      // agent cash collection - these are two different pots of money and giving
      // them the same nav entry would guarantee they get confused.
      { label: 'Wallet & Referrals', to: '/wallet', icon: Gift, perm: 'wallet.view' },
      { label: 'Expenses', to: '/expenses', icon: BarChart3, perm: 'expenses.manage' },
      { label: 'Complaints', to: '/complaints', icon: AlertTriangle, perm: 'complaints.view' },
      { label: 'Disputes', to: '/disputes', icon: ShieldAlert, perm: 'complaints.view' },
      { label: 'SLA Monitor', to: '/sla-monitor', icon: AlertTriangle, perm: 'orders.view' },
      { label: 'Coupons', to: '/management?tab=coupons', icon: Tag, perm: 'coupons.create' },
      { label: 'WhatsApp Blast', to: '/broadcast', icon: Megaphone, perm: 'broadcast.send', flag: 'whatsappBlast' },
      { label: 'Marketing', to: '/marketing', icon: BarChart3, perm: 'marketing.view' },
    ],
  },
  {
    heading: 'SYSTEM',
    items: [
      { label: 'Settings', to: '/settings', icon: Settings, perm: 'settings.update' },
      { label: 'Activity Log', to: '/activity-log', icon: ClipboardList, perm: 'analytics.view' },
    ],
  },
]

const getInitials = (name: string | undefined) => {
  if (!name) {
    return 'AD'
  }

  return name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

interface SidebarProps {
  isOpen?: boolean
  onLinkClick?: () => void
}

export default function Sidebar({ isOpen, onLinkClick }: SidebarProps) {
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const colors = useColors()
  const { flags } = useFeatureFlags()
  const adminRole = user?.admin_role || 'operator'

  // Gate each nav item by the exact backend permission AND any runtime feature
  // flag. Items without a `perm` (e.g. the dashboard) are visible to every
  // authenticated admin; items with a `flag` are hidden while that flag is off.
  const filteredSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) =>
        (!item.perm || userHasPermission(user, item.perm))
        && (!item.flag || flags[item.flag])),
    }))
    .filter((section) => section.items.length > 0)

  const roleBadge: Record<string, { bg: string; color: string; label: string }> = {
    owner: { bg: '#D6B97B', color: '#0F2E2A', label: 'Owner' },
    manager: { bg: '#3B82F6', color: '#FFFFFF', label: 'Manager' },
    accountant: { bg: '#10B981', color: '#FFFFFF', label: 'Accountant' },
    operator: { bg: '#6B7280', color: '#FFFFFF', label: 'Operator' },
  }
  const badge = roleBadge[adminRole] ?? roleBadge.operator
  const roleBadgeBg = badge.bg
  const roleBadgeColor = badge.color
  const roleBadgeLabel = badge.label
  const RoleBadgeIcon =
    adminRole === 'owner' ? ShieldAlert : adminRole === 'manager' ? Wrench : User
  const [activeExpressCount, setActiveExpressCount] = useState(0)

  useEffect(() => {
    let mounted = true

    const loadExpressCount = async () => {
      try {
        const res = await adminApi.getExpressSlots()
        const data = res?.data ?? res
        const slots: Array<{ orders?: unknown[] }> = Array.isArray(data?.slots) ? data.slots : []
        const orders = slots.flatMap((slot) => (Array.isArray(slot.orders) ? slot.orders : []))
        if (mounted) {
          setActiveExpressCount(orders.length)
        }
      } catch {
        if (mounted) {
          setActiveExpressCount(0)
        }
      }
    }

    void loadExpressCount()
    const intervalId = window.setInterval(() => {
      void loadExpressCount()
    }, 30_000)

    return () => {
      mounted = false
      window.clearInterval(intervalId)
    }
  }, [])

  return (
    <aside
      className={`admin-sidebar sidebar ${isOpen ? 'is-open open' : ''}`}
      style={{
        width: '240px',
        background: colors.bgSidebar,
        color: colors.textPrimary,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
      }}
    >
      <div style={{ padding: '24px' }}>
        <div
          className="font-display"
          style={{
            color: colors.accent,
            fontSize: '20px',
            fontWeight: 700,
            marginBottom: '4px',
          }}
        >
          RAPIDRY
        </div>
        <div
          className="font-body"
          style={{
            color: 'rgba(243, 239, 230, 0.8)',
            fontSize: '11px',
            fontWeight: 400,
          }}
        >
          Admin Portal
        </div>
      </div>

      <nav
        style={{
          flex: 1,
          overflowY: 'auto',
          position: 'relative',
        }}
      >
        {filteredSections.map((section) => (
          <div key={section.heading}>
            <p
              className="font-body"
              style={{
                fontSize: '10px',
                fontWeight: 500,
                letterSpacing: '1px',
                color: 'rgba(243, 239, 230, 0.78)',
                padding: '24px 20px 8px',
                margin: 0,
              }}
            >
              {section.heading}
            </p>

            {section.items.map((item) => (
              <NavLink
                key={`${section.heading}-${item.label}`}
                to={item.to}
                end={item.to === '/'}
                className="font-body"
                onClick={() => onLinkClick?.()}
                style={({ isActive }) => ({
                  width: 'calc(100% - 16px)',
                  margin: '2px 8px',
                  borderRadius: '10px',
                  padding: '10px 16px',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  textDecoration: 'none',
                  color: '#F3EFE6',
                  opacity: isActive ? 1 : 0.7,
                  fontWeight: isActive ? 600 : 400,
                  background: isActive ? 'rgba(214,185,123,0.12)' : 'transparent',
                  position: 'relative',
                  fontSize: '14px',
                  transition: 'background 0.2s ease, opacity 0.2s ease',
                })}
              >
                {({ isActive }) => (
                  <>
                    {isActive ? (
                      <span
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: '8px',
                          width: '3px',
                          height: '24px',
                          borderRadius: '0 999px 999px 0',
                          background: colors.accent,
                        }}
                      />
                    ) : null}
                    <item.icon
                      size={18}
                      color={isActive ? colors.accent : 'rgba(243, 239, 230, 0.6)'}
                    />
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                      <span>{item.label}</span>
                      {item.to === '/express-orders' && activeExpressCount > 0 ? (
                        <span
                          className="font-body"
                          style={{
                            background: colors.accent,
                            color: colors.bgSidebar,
                            borderRadius: '999px',
                            padding: '1px 7px',
                            fontSize: '11px',
                            fontWeight: 700,
                            lineHeight: 1.5,
                          }}
                        >
                          {activeExpressCount}
                        </span>
                      ) : null}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      <div
        style={{
          borderTop: `1px solid ${colors.border}`,
          padding: '20px 16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <div
            className="font-body"
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '999px',
              background: colors.accent,
              color: colors.bgSidebar,
              fontSize: '13px',
              fontWeight: 600,
              display: 'grid',
              placeItems: 'center',
              flexShrink: 0,
            }}
          >
            {getInitials(user?.name)}
          </div>

          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              className="font-body"
              style={{
                fontSize: '13px',
                fontWeight: 600,
                color: '#F3EFE6',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.name || 'Admin'}
            </div>
            <a
              href="https://wa.me/917667625880"
              target="_blank"
              rel="noreferrer"
              className="font-body"
              style={{
                marginTop: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                fontSize: '11px',
                color: colors.accent,
                textDecoration: 'none',
              }}
              onMouseEnter={(event) => {
                event.currentTarget.style.textDecoration = 'underline'
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.textDecoration = 'none'
              }}
            >
              Rapidry WhatsApp
            </a>
            <div
              className="font-body"
              style={{
                fontSize: '11px',
                fontWeight: 400,
                color: 'rgba(243, 239, 230, 0.78)',
              }}
            >
              {roleBadgeLabel}
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            aria-label="Logout"
            style={{
              border: 'none',
              background: 'transparent',
              color: '#F3EFE6',
              cursor: 'pointer',
              padding: 0,
              display: 'grid',
              placeItems: 'center',
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.color = '#EF4444'
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.color = '#F3EFE6'
            }}
          >
            <LogOut size={16} />
          </button>
        </div>
        <div
          className="font-body"
          style={{
            marginTop: '16px',
            padding: '6px 12px',
            borderRadius: '999px',
            background: roleBadgeBg,
            color: roleBadgeColor,
            fontSize: '11px',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <RoleBadgeIcon size={14} />
          {roleBadgeLabel}
        </div>
        <div
          className="font-body"
          style={{
            marginTop: '8px',
            fontSize: '11px',
            color: 'rgba(243, 239, 230, 0.78)',
            textAlign: 'center',
            cursor: 'pointer'
          }}
          onClick={() => document.dispatchEvent(new CustomEvent('show-shortcuts'))}
        >
          Keyboard shortcuts: <kbd style={{ padding: '1px 4px', background: 'rgba(0,0,0,0.1)', borderRadius: '3px' }}>?</kbd>
        </div>
      </div>
    </aside>
  )
}
